import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Drawer } from '@material-ui/core';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import DynamicFeedOutlinedIcon from '@material-ui/icons/DynamicFeedOutlined';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';

import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import PowerSettingsNewOutlinedIcon from '@material-ui/icons/PowerSettingsNewOutlined';
import QuestionAnswerOutlinedIcon from '@material-ui/icons/QuestionAnswerOutlined';
import ViewCompactOutlinedIcon from '@material-ui/icons/ViewCompactOutlined';
import SpeakerNotesOutlinedIcon from '@material-ui/icons/SpeakerNotesOutlined';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import FindInPageOutlinedIcon from '@material-ui/icons/FindInPageOutlined';
import FormatListBulletedOutlinedIcon from '@material-ui/icons/FormatListBulletedOutlined';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';
import PeopleAltOutlinedIcon from '@material-ui/icons/PeopleAltOutlined';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import UnsubscribeIcon from '@material-ui/icons/Unsubscribe';
import PersonPinCircleOutlinedIcon from '@material-ui/icons/PersonPinCircleOutlined';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import { actions as UserActions } from '../../../../store/user';

import { SidebarNav } from './components';
import Skeleton from 'react-loading-skeleton';
import LicenseCheck from 'LicenseCheck';
import { SideBarNavTitle } from 'constants.js';
import { getEnvVar, isDashboardEnabledForTenant } from 'helpers';
import { AccountTreeOutlined, RecordVoiceOverOutlined } from '@material-ui/icons';
import { useLanguageSettings } from '../../../../views/Settings/useLanguageSettings';

const { logout } = UserActions;

const useStyles = makeStyles(theme => ({
  drawer: {
    width: 240,
    [theme.breakpoints.up('lg')]: {
      marginTop: 64,
      height: 'calc(100% - 64px)'
    }
  },
  root: {
    backgroundColor: theme.palette.white,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(2)
  },
  divider: {
    margin: theme.spacing(2, 0)
  },
  nav: {
    marginBottom: theme.spacing(2)
  }
}));

const Sidebar = props => {
  const { open, variant, onClose, className, ...rest } = props;
  const dispatch = useDispatch();
  const { languageContent, getLanguageContent } = useLanguageSettings();
  const [sideBarNavTitleContent, setSideBarNavTitleContent] = useState(null);
  const selectedTenantId = useSelector(store => store.user.selectedTenant?.tenantId);

  const licenses = LicenseCheck();
  const { isLicenseLoading } = licenses;
  const isDashboardEnabled = isDashboardEnabledForTenant(
    selectedTenantId ?? window.localStorage.getItem('GCM-tenantId')
  );

  const classes = useStyles();

  useEffect(() => {
    if (languageContent) {
      setSideBarNavTitleContent(getLanguageContent("sideBarNavTitle"));
    }
  }, [languageContent]);

  const templateSidebarPages = [
    {
      title: sideBarNavTitleContent?.communication || SideBarNavTitle.Communication,
      key: SideBarNavTitle.Communication,
      href: '/templates',
      icon: <EmailOutlinedIcon />
    },
    {
      title: sideBarNavTitleContent?.attachments || SideBarNavTitle.Attachments,
      key: SideBarNavTitle.Attachments,
      href: '/attachment-templates',
      icon: <AttachFileIcon />
    },
    {
      title: sideBarNavTitleContent?.printTemplates || SideBarNavTitle.PrintTemplates,
      key: SideBarNavTitle.PrintTemplates,
      href: '/print-templates',
      icon: <PrintOutlinedIcon />
    },
    {
      title: sideBarNavTitleContent?.surveyResultResponseTemplates || SideBarNavTitle.SurveyResultResponseTemplates,
      key: SideBarNavTitle.SurveyResultResponseTemplates,
      href: '/surveyResultResponse-templates',
      icon: <QuestionAnswerOutlinedIcon />
    }
  ];

  const reportsSidebarPages = [
    {
      title: sideBarNavTitleContent?.summary || SideBarNavTitle.Summary,
      key: SideBarNavTitle.Summary,
      href: '/reports',
      icon: <DescriptionOutlinedIcon />
    },
    {
      title: sideBarNavTitleContent?.unSubscribe || SideBarNavTitle.UnSubscribe,
      key: SideBarNavTitle.UnSubscribe,
      href: '/unsubscribe',
      icon: <UnsubscribeIcon />
    },
    {
      title: sideBarNavTitleContent?.events || SideBarNavTitle.Events,
      key: SideBarNavTitle.Events,
      href: '/events',
      icon: <EventOutlinedIcon />
    },
    {
      title: sideBarNavTitleContent?.batches || SideBarNavTitle.Batches,
      key: SideBarNavTitle.Batches,
      href: '/batches',
      icon: <DynamicFeedOutlinedIcon />
    }
  ];

  const campaignsSidebarPages = [
    {
      title: sideBarNavTitleContent?.allCampaigns || SideBarNavTitle.AllCampaigns,
      key: SideBarNavTitle.AllCampaigns,
      href: '/campaigns',
      icon: <RecordVoiceOverOutlined />
    },
    {
      title: sideBarNavTitleContent?.recipientQueries || SideBarNavTitle.RecipientQueries,
      key: SideBarNavTitle.RecipientQueries,
      href: '/recipient-queries',
      icon: <FindInPageOutlinedIcon />
    },
  ];

  const pages = [
    isDashboardEnabled && {
      title: sideBarNavTitleContent?.dashboard || SideBarNavTitle.Dashboard,
      key: SideBarNavTitle.Dashboard,
      href: '/dashboard',
      icon: <DashboardOutlinedIcon />
    },
    {
      title: sideBarNavTitleContent?.campaigns || SideBarNavTitle.Campaigns,
      key: SideBarNavTitle.Campaigns,
      icon: <PeopleAltOutlinedIcon />,
      isCategory: true,
      pages: campaignsSidebarPages
    },
    {
      title: sideBarNavTitleContent?.templates || SideBarNavTitle.Templates,
      key: SideBarNavTitle.Templates,
      icon: <ViewCompactOutlinedIcon />,
      isCategory: true,
      pages: templateSidebarPages
    },
    {
      title: sideBarNavTitleContent?.uploads || SideBarNavTitle.Uploads,
      key: SideBarNavTitle.Uploads,
      href: '/uploads',
      icon: <CloudUploadOutlinedIcon />
    },
    {
      title: sideBarNavTitleContent?.conditionQueries || SideBarNavTitle.ConditionQueries,
      key: SideBarNavTitle.ConditionQueries,
      href: '/condition-queries',
      icon: <AccountTreeOutlined />
    },
    getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT') === 'true' && {
      title: sideBarNavTitleContent?.listsAndSubscribers || SideBarNavTitle.ListsAndSubscribers,
      key: SideBarNavTitle.ListsAndSubscribers,
      href: '/lists',
      icon: <FormatListBulletedOutlinedIcon />
    },
    {
      title: sideBarNavTitleContent?.guestInsights || SideBarNavTitle.GuestInsights,
      key: SideBarNavTitle.GuestInsights,
      href: '/guest-insights',
      icon: <PersonPinCircleOutlinedIcon />
    },
    {
      title: sideBarNavTitleContent?.reports || SideBarNavTitle.Reports,
      key: SideBarNavTitle.Reports,
      icon: <AssessmentOutlinedIcon />,
      isCategory: true,
      pages: reportsSidebarPages
    },
    {
      title: sideBarNavTitleContent?.surveys || SideBarNavTitle.Surveys,
      key: SideBarNavTitle.Surveys,
      href: '/surveys',
      icon: <SpeakerNotesOutlinedIcon />
    },
    {
      title: sideBarNavTitleContent?.settings || SideBarNavTitle.Settings,
      key: SideBarNavTitle.Settings,
      href: '/settings',
      icon: <SettingsOutlinedIcon />
    },
    {
      title: sideBarNavTitleContent?.logout || SideBarNavTitle.Logout,
      key: SideBarNavTitle.Logout,
      href: '/sign-in',
      action: () => {
        dispatch(logout());
      },
      icon: <PowerSettingsNewOutlinedIcon />
    }
  ];

  return (
    <Drawer
      anchor="left"
      classes={{ paper: classes.drawer }}
      onClose={onClose}
      open={open}
      variant={variant}>
      {isLicenseLoading && <Skeleton />}
      {!isLicenseLoading && (
        <div {...rest} className={clsx(classes.root, className)}>
          <SidebarNav className={classes.nav} pages={pages} />
        </div>
      )}
    </Drawer>
  );
};

Sidebar.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
  variant: PropTypes.string.isRequired
};

export default Sidebar;
